import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { PREFERENCES, THEMES, swatches } from '../theme/themeStore';

/*
 * Appearance choice: match the system, or one of the three themes.
 *
 * `inverse` renders on the dark green mobile menu. Each option carries its own
 * preview swatches (page, surface, primary, text) taken from the palette, and a
 * visible name, so no option is identified by colour alone.
 *
 * `variant="page"` is the settings page's form: no caption of its own (the
 * page row names the group, through `labelledBy`), and options sized like the
 * page's other controls -- 14 px text and 44 px tall -- instead of the
 * compact menu's.
 */
export default function ThemePicker({ inverse = false, variant = 'menu', labelledBy }) {
  const { t } = useTranslation('theme');
  const { preference, setPreference } = useTheme();
  const page = variant === 'page';

  return (
    <div role="radiogroup" aria-label={labelledBy ? undefined : t('title')} aria-labelledby={labelledBy}
         data-testid="theme-picker" data-variant={variant}
         style={page ? { display: 'flex', flexWrap: 'wrap', gap: 8 } : undefined}>
      {!page && (
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                      color: inverse ? 'rgba(255,255,255,0.75)' : 'var(--c-text-muted-fg)', margin: '2px 0 4px' }}>
          {t('title')}
        </div>
      )}
      {PREFERENCES.map(option => {
        const selected = preference === option;
        if (page) {
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setPreference(option)}
              title={t(`description.${option}`)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '8px 14px',
                borderRadius: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', textAlign: 'start',
                fontWeight: selected ? 600 : 500,
                border: `1px solid ${selected ? 'var(--c-primary)' : 'var(--c-border-strong)'}`,
                background: selected ? 'var(--c-primary-soft)' : 'var(--c-surface)',
                color: 'var(--c-text-fg)',
              }}
            >
              <Preview option={option} />
              <span>{t(`option.${option}`)}</span>
              {selected && <span aria-hidden="true">✓</span>}
            </button>
          );
        }
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => setPreference(option)}
            title={t(`description.${option}`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
              padding: '5px 6px', marginBottom: 2, borderRadius: 6, cursor: 'pointer', fontSize: 12,
              border: `1px solid ${selected ? (inverse ? 'rgba(255,255,255,0.7)' : 'var(--c-primary)') : 'transparent'}`,
              background: selected ? (inverse ? 'rgba(255,255,255,0.18)' : 'var(--c-primary-soft)') : 'transparent',
              color: inverse ? 'var(--c-on-accent)' : 'var(--c-text-fg)',
            }}
          >
            <Preview option={option} />
            <span style={{ flex: 1 }}>{t(`option.${option}`)}</span>
            {selected && <span aria-hidden="true">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

function Preview({ option }) {
  const themes = option === 'system' ? ['lab_light', 'field_dark'] : [option];
  return (
    <span aria-hidden="true" style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden',
                                      border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }}>
      {themes.filter(name => THEMES.includes(name)).map(name => {
        const s = swatches(name);
        return (
          <span key={name} style={{ display: 'inline-flex', width: themes.length > 1 ? 14 : 28, height: 16,
                                    background: s.page, alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <span style={{ width: 5, height: 10, background: s.primary, borderRadius: 1 }} />
            {themes.length === 1 && <span style={{ width: 9, height: 10, background: s.surface, borderRadius: 1,
                                                   boxShadow: `inset 0 -3px 0 ${s.text}` }} />}
          </span>
        );
      })}
    </span>
  );
}
